import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search as SearchIcon, Bookmark, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/control/PageHeader";
import { api } from "@/lib/api";
import {
  Section, Loadable, Empty, Status, ActionButton, RunButton, SelectField, TextField, useLoad, COUNTRY_OPTIONS,
} from "@/components/control/kit";

const AREAS = [
  { value: "all", label: "Alle Bereiche" }, { value: "activities", label: "Aktivitäten" },
  { value: "groups", label: "Kreise & Gruppen" }, { value: "content", label: "Inhalte" },
  { value: "organizations", label: "Organisationen" },
];
const COUNTRIES = [{ value: "all", label: "Alle Länder" }, ...COUNTRY_OPTIONS];

function Results({ path }) {
  const st = useLoad(path);
  return (
    <Loadable state={st}>
      {(d) => (d.total === 0 ? <Empty text={`Keine Treffer für „${d.query}“.`} testId="search-empty" /> : (
        <div className="space-y-5" data-testid="search-results">
          {d.groups.filter((g) => g.count > 0).map((g) => (
            <Section key={g.area} title={`${g.label} (${g.count})`} testId={`search-group-${g.area}`}>
              <ul className="divide-y divide-border">
                {g.hits.map((h) => (
                  <li key={h.id} className="flex items-center justify-between gap-3 py-2.5">
                    <Link to={h.route} className="font-medium text-brand-purple hover:underline" data-testid={`search-hit-${h.id}`}>{h.title || "Ohne Titel"}</Link>
                    <span className="flex items-center gap-2 text-xs text-muted-foreground">{h.country}{h.status && <Status value={h.status} />}</span>
                  </li>
                ))}
              </ul>
            </Section>
          ))}
          <p className="text-xs text-muted-foreground">Private Nachrichten, Access-Pass-Daten und interne Notizen werden nie durchsucht.</p>
        </div>
      ))}
    </Loadable>
  );
}

function SavedViews({ current, apply }) {
  const st = useLoad("/saved-views?area=suche");
  return (
    <Section title="Gespeicherte Arbeitsansichten" testId="saved-views" actions={
      <ActionButton label="Ansicht speichern" icon={Bookmark} testId="save-view" onDone={st.reload}
        description="Speichert Suchbegriff und Filter. Wähle ausdrücklich, wer die Ansicht sehen darf."
        fields={[
          { name: "name", label: "Name der Ansicht", required: true },
          { name: "visibility", label: "Sichtbar für", type: "select", required: true,
            options: [{ value: "me", label: "Nur für mich" }, { value: "team", label: "Für mein Team (gleiche Rolle)" }] },
        ]}
        onSubmit={async (v) => { await api.post("/saved-views", { ...v, area: "suche", filters: current }); return "Ansicht gespeichert."; }} />
    }>
      <Loadable state={st}>
        {(d) => (d.views.length === 0 ? <Empty text="Noch keine gespeicherten Ansichten." /> : (
          <ul className="divide-y divide-border">
            {d.views.map((v) => (
              <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`saved-view-${v.id}`}>
                <div>
                  <p className="font-medium text-brand-purple">{v.name}</p>
                  <p className="text-xs text-muted-foreground">„{v.filters.q}“ · {v.visibility === "team" ? "Team" : "Nur ich"} · {v.owner_name}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => apply(v.filters)} data-testid={`apply-view-${v.id}`}>Anwenden</Button>
                  {v.mine && <RunButton label="Löschen" icon={Trash2} testId={`delete-view-${v.id}`} success="Ansicht gelöscht."
                    run={() => api.delete(`/saved-views/${v.id}`)} onDone={st.reload} />}
                </div>
              </li>
            ))}
          </ul>
        ))}
      </Loadable>
    </Section>
  );
}

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [area, setArea] = useState(params.get("area") || "all");
  const [country, setCountry] = useState(params.get("country") || "all");
  const active = (params.get("q") || "").trim();
  const path = active.length >= 2
    ? `/search?${new URLSearchParams({ q: active, area: params.get("area") || "all", country: params.get("country") || "all" })}` : null;

  const run = (f) => setParams(f);
  const apply = (f) => { setQ(f.q || ""); setArea(f.area || "all"); setCountry(f.country || "all"); run(f); };

  return (
    <div>
      <PageHeader title="Globale Suche" subtitle="Durchsucht nur Bereiche, für die du berechtigt bist." icon={SearchIcon} />
      <form onSubmit={(e) => { e.preventDefault(); run({ q, area, country }); }}
        className="mb-5 grid gap-3 rounded-xl border border-border bg-white p-4 sm:grid-cols-[1fr_200px_180px_auto] sm:items-end">
        <TextField id="search-q" label="Suchbegriff" value={q} onChange={setQ} testId="search-input" placeholder="mindestens zwei Zeichen" />
        <SelectField id="search-area" label="Bereich" value={area} onChange={setArea} options={AREAS} testId="search-area" />
        <SelectField id="search-country" label="Land" value={country} onChange={setCountry} options={COUNTRIES} testId="search-country" />
        <Button type="submit" data-testid="search-submit" className="h-10 bg-brand-magenta hover:bg-brand-magenta-dark">Suchen</Button>
      </form>
      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <div>{path ? <Results path={path} /> : <Empty text="Bitte mindestens zwei Zeichen eingeben." testId="search-hint" />}</div>
        <SavedViews current={{ q: active, area: params.get("area") || "all", country: params.get("country") || "all" }} apply={apply} />
      </div>
    </div>
  );
}
